import type { FunctionComponent } from 'react';
import React, { useCallback } from 'react';
import cx from 'classnames';

import type { EventContainer } from '@trello/atlassian-analytics';
import { Analytics } from '@trello/atlassian-analytics';
import { isEmbeddedInAtlassian } from '@trello/browser';
import { dynamicToken } from '@trello/dynamic-tokens';
import { forTemplate } from '@trello/i18n';
import { Tooltip } from '@trello/nachos/tooltip';
import type { TestId } from '@trello/test-ids';
import { BoardHeaderTestIds } from '@trello/test-ids';

import { RouterLink } from 'app/src/components/RouterLink';
import { getViewUrl } from './getViewUrl';
import type { SwitcherView_ViewType, ViewOption } from './ViewSwitcher';

// eslint-disable-next-line @trello/less-matches-component
import styles from './ViewSwitcher.less';

const format = forTemplate('view_switcher');

interface ViewSwitcherButtonProps {
  idBoard: string;
  currentView: SwitcherView_ViewType | null;
  viewType: SwitcherView_ViewType;
  viewOption: ViewOption;
  analyticsContainers: EventContainer;
  isCollapsed: boolean;
}

const ViewSwitcherButtonsTestIdsMap: {
  [key in SwitcherView_ViewType]: TestId;
} = {
  Table: BoardHeaderTestIds.ViewSwitcherButtonTable,
  Board: BoardHeaderTestIds.ViewSwitcherButtonBoard,
  Calendar: BoardHeaderTestIds.ViewSwitcherButtonCalendar,
  Dashboard: BoardHeaderTestIds.ViewSwitcherButtonDashboard,
  Timeline: BoardHeaderTestIds.ViewSwitcherButtonTimeline,
  Map: BoardHeaderTestIds.ViewSwitcherButtonMap,
};

export const ViewSwitcherButton: FunctionComponent<ViewSwitcherButtonProps> = ({
  idBoard,
  currentView,
  viewType,
  viewOption,
  analyticsContainers,
  isCollapsed,
}) => {
  const view = viewOption;
  const currentlyOnView = viewType === currentView;
  const testId = ViewSwitcherButtonsTestIdsMap[viewType];

  const onViewClick = useCallback(
    (currentlyOnView: boolean, view: ViewOption) => {
      return () => {
        if (!currentlyOnView) {
          Analytics.sendClickedButtonEvent({
            buttonName: 'boardViewSwitcherButton',
            source: 'boardScreen',
            containers: analyticsContainers,
            attributes: {
              viewClicked: view.name,
            },
          });
        }
      };
    },
    [analyticsContainers],
  );

  const onKeyUp = useCallback(
    (e: React.KeyboardEvent<Element>, callback: () => void) => {
      if (e.key === 'Enter') {
        callback();
      }
    },
    [],
  );

  const isHighlighted =
    !isEmbeddedInAtlassian() && currentlyOnView && !isCollapsed;

  return (
    <Tooltip content={format(viewType)}>
      <RouterLink
        href={getViewUrl(idBoard, view.name)}
        key={viewType}
        tabIndex={0}
        role={'link'}
        onClick={onViewClick(currentlyOnView, view)}
        aria-label={format(viewType)}
        testId={testId}
        // eslint-disable-next-line react/jsx-no-bind
        onKeyUp={(e) => onKeyUp(e, onViewClick(currentlyOnView, view))}
        className={cx(
          styles.viewSwitcherButton,
          isHighlighted && styles.highlighted,
          isCollapsed && styles.isCollapsed,
        )}
      >
        <span
          className={styles.viewSwitcherIcon}
          data-testid={BoardHeaderTestIds.ViewSwitcherButtonIcon}
        >
          {React.createElement(view.icon, {
            size: 'small',
            color: isHighlighted
              ? dynamicToken('dynamic.button.highlighted.text')
              : dynamicToken('dynamic.text'),
          })}
        </span>
        <span
          data-testid={BoardHeaderTestIds.ViewSwitcherButtonText}
          className={styles.buttonText}
        >
          {format(viewType)}
        </span>
      </RouterLink>
    </Tooltip>
  );
};
